import React, { useState, useContext } from "react";
import { FieldConfigurationContext } from "../../../../../lib/context/FieldConfigurationContext";
import TextInput from "../../../../common/TextInput";
import TextArea from "../../../../common/TextArea";
import Button from "../../../../common/Button";
import Validation from "../../../../common/Validation";
import DetailHeader from "../../components/DetailHeader";
import LicenseErrorModal from "../../components/LicenseErrorModal";
import TimeTab from "./TimeTab";
import ExpenseTab from "./ExpenseTab";
import ProjectTab from "./ProjectTab";
import "./RoleForm.css";

const initialRoleState = {
  name: "",
  description: "",
  enableJobLocationEditing: false,

  // Time Entry fields
  enableTime: false,
  allowMultipleEmployeeEntries: false,
  allowSigningOtherEmployees: false,
  showMyEmployeesWeeklySummary: false,
  showMyEmployeesWorkerClass: false,
  allowApprovals: false,
  allowAbsenceEntries: false,
  viewAndSignOnly: false,
  showPayType: false,
  payTypeOptions: {
    regular: true, // Always enabled by default
    overtime: false,
    double: false,
    sick: false,
    vacation: false,
    holiday: false,
    bonus: false,
    other: false,
    nontax: false,
  },
  requireTimer: false,
  requireJob: false,
  showWorkersClass: false,
  showDepartment: false,
  showWorkLocation: false,
  showEquipment: false,
  showUnits: false,
  timerAlertMinutes: 0,
  hoursAlertHours: 0,

  // Expense fields
  enableExpense: false,

  // Project Management fields
  enableProjectManagement: false,
  allowFieldLogs: false,

  // Administration permissions
  enableAdministration: false,
  adminViewLicenses: false,
  adminConfigureAccessGroups: false,
  adminConfigureRoles: false,
  adminConfigureUsers: false,
};

export default function RoleForm({ role, onSave, onCancel }) {
  const {
    createRole,
    updateRole,
    bulkUpdateRoles,
    saveRoleWithCompanyAssignments,
    companies,
  } = useContext(FieldConfigurationContext);
  const [formData, setFormData] = useState(
    role ? { ...initialRoleState, ...role } : initialRoleState
  );
  const [activeTab, setActiveTab] = useState("time");
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [licenseErrors, setLicenseErrors] = useState([]);
  const [licenseErrorDetails, setLicenseErrorDetails] = useState([]);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [success, setSuccess] = useState("");

  const isEditing = !!role;
  const configureRolesData = companies?.configureRolesData || {};

  const manageableUuidsList = Object.keys(configureRolesData);
  const showCompanySection = manageableUuidsList.length > (isEditing ? 0 : 1);

  // Track which companies should be assigned after save (only within manageable companies)
  const [selectedCompanyUuids, setSelectedCompanyUuids] = useState(
    () =>
      role
        ? (role.companyUuids || []).filter((uuid) => configureRolesData[uuid])
        : manageableUuidsList // Default all selected when creating
  );

  const handleCompanyToggle = (uuid) => {
    setSelectedCompanyUuids((prev) =>
      prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid]
    );
  };

  // Handle toggling the Enable Administration parent checkbox (cascades to all sub-permissions)
  const handleAdministrationEnabledChange = (checked) => {
    setFormData((prev) => ({
      ...prev,
      enableAdministration: checked,
      adminViewLicenses: checked,
      adminConfigureAccessGroups: checked,
      adminConfigureRoles: checked,
      adminConfigureUsers: checked,
    }));
    if (licenseErrors.length > 0) {
      setLicenseErrors([]);
      setLicenseErrorDetails([]);
      setShowLicenseModal(false);
    }
    if (error) setError("");
  };

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    // Clear license errors when making changes
    if (licenseErrors.length > 0) {
      setLicenseErrors([]);
      setLicenseErrorDetails([]);
      setShowLicenseModal(false);
    }
    // Clear general error message when making changes
    if (error) {
      setError("");
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Role name is required";
    } else if (formData.name.length > 30) {
      newErrors.name = "Role name must be 30 characters or less";
    }

    if (formData.description && formData.description.length > 250) {
      newErrors.description = "Description must be 250 characters or less";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle cancel with confirmation
  const handleCancel = () => {
    const confirmed = window.confirm("Are you sure you want to cancel?");
    if (confirmed) {
      onCancel();
    }
  };

  // Handle save
  const handleSave = async () => {
    const willDeleteRole =
      isEditing &&
      showCompanySection &&
      selectedCompanyUuids.length === 0 &&
      (role?.companyUuids || []).filter((uuid) => !configureRolesData[uuid])
        .length === 0;

    if (willDeleteRole) {
      const confirmed = window.confirm(
        `Removing all company assignments will permanently delete this role. Are you sure you want to continue?`
      );
      if (!confirmed) return;
    }

    if (!isEditing && showCompanySection && selectedCompanyUuids.length === 0) {
      setError("Please select at least one company to assign this role to.");
      return;
    }

    if (!validate()) {
      setError("Please fix the errors before saving.");
      setSuccess("");
      setLicenseErrors([]);
      setLicenseErrorDetails([]);
      return;
    }

    setIsSaving(true);
    setError("");
    setLicenseErrors([]);
    setLicenseErrorDetails([]);
    setSuccess("");

    try {
      // Ensure regular pay type is always enabled
      const dataToSave = {
        ...formData,
        payTypeOptions: {
          ...formData.payTypeOptions,
          regular: true,
        },
      };

      const result = isEditing
        ? manageableUuidsList.length > 0
          ? await saveRoleWithCompanyAssignments(
              role,
              dataToSave,
              selectedCompanyUuids,
              manageableUuidsList,
              companies.data
            )
          : role.uuids?.length > 1
          ? await bulkUpdateRoles(role.uuids, dataToSave)
          : await updateRole(role.uuid, dataToSave)
        : showCompanySection
        ? await saveRoleWithCompanyAssignments(
            { companyUuids: [], uuids: [] },
            dataToSave,
            selectedCompanyUuids,
            manageableUuidsList,
            companies.data
          )
        : await createRole(dataToSave);

      if (result.success) {
        setSuccess(`Role ${isEditing ? "updated" : "created"} successfully!`);
        setTimeout(() => {
          onSave && onSave(result.data);
        }, 1000);
      } else if (
        result.status === 403 &&
        result.errors &&
        result.errors.length > 0
      ) {
        // Handle license limit errors with modal
        setError(
          result.error ||
            "Insufficient license seats to enable requested features."
        );
        setLicenseErrors(result.errors);
        setLicenseErrorDetails(result.details || []);
        setShowLicenseModal(true);
      } else {
        setError(
          result.error ||
            `Failed to ${
              isEditing ? "update" : "create"
            } role. Please try again.`
        );
      }
    } catch (err) {
      setError(`Network error. Please check your connection and try again.`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="role-form">
      <DetailHeader
        title={isEditing ? "Edit Role" : "Add New Role"}
        onBack={handleCancel}
        onSave={handleSave}
        isEditing={true}
        saveDisabled={isSaving}
        saveLabel={isSaving ? "Saving..." : "Save Role"}
        cancelLabel="Cancel"
      />

      {/* Error and Success Messages */}
      {error && !showLicenseModal && (
        <div style={{ padding: "0 24px", marginTop: "16px" }}>
          <Validation className="danger">{error}</Validation>
        </div>
      )}
      {success && (
        <div style={{ padding: "0 24px", marginTop: "16px" }}>
          <Validation className="success">{success}</Validation>
        </div>
      )}

      {/* License Error Modal */}
      <LicenseErrorModal
        isOpen={showLicenseModal}
        onClose={() => setShowLicenseModal(false)}
        roleUuid={role?.uuid}
        errorMessage={error}
        details={licenseErrorDetails}
        context="role"
      />

      <div className="role-form__content">
        {/* Basic Role Information */}
        <div className="role-form__section role-form__section--info">
          <h3 className="role-form__section-title">Role Information</h3>
          <div className="role-form__fields">
            <div>
              <TextInput
                label="Role Name"
                required
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Enter role name (max 30 characters)"
                disabled={isSaving}
              />
              <div className="role-form__char-count">
                {(formData.name || "").length}/30 characters
              </div>
              {errors.name && (
                <div className="role-form__error">{errors.name}</div>
              )}
            </div>
            <div>
              <TextArea
                label="Description"
                value={formData.description || ""}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Enter role description (max 250 characters)"
                rows={3}
                disabled={isSaving}
              />
              <div className="role-form__char-count">
                {(formData.description || "").length}/250 characters
              </div>
              {errors.description && (
                <div className="role-form__error">{errors.description}</div>
              )}
            </div>
          </div>
        </div>

        {/* Company Assignments */}
        {showCompanySection && (
          <div className="role-form__section">
            <h3 className="role-form__section-title">Company Assignments</h3>
            <div className="role-form__company-badges">
              {Object.entries(configureRolesData).map(([uuid, name]) => {
                const isSelected = selectedCompanyUuids.includes(uuid);
                const code = companies?.codesMap?.[uuid];
                return (
                  <button
                    key={uuid}
                    type="button"
                    className={`role-form__company-badge${
                      isSelected ? " role-form__company-badge--assigned" : ""
                    }`}
                    onClick={() => handleCompanyToggle(uuid)}
                    disabled={isSaving}
                  >
                    {code != null ? `${name} (${code})` : name}
                  </button>
                );
              })}
            </div>
            {isEditing &&
              selectedCompanyUuids.length === 0 &&
              (role?.companyUuids || []).filter(
                (uuid) => !configureRolesData[uuid]
              ).length === 0 && (
                <div className="role-form__delete-warning">
                  Warning: Removing all company assignments will permanently
                  delete this role.
                </div>
              )}
          </div>
        )}

        {/* General Settings */}
        <div className="role-form__section">
          <h3 className="role-form__section-title">General Settings</h3>
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.enableJobLocationEditing}
              onChange={(e) =>
                handleChange("enableJobLocationEditing", e.target.checked)
              }
              disabled={isSaving}
            />
            <span>Enable Job Location Editing</span>
          </label>
          <label className="role-form__checkbox-label">
            <input
              type="checkbox"
              checked={formData.enableAdministration}
              onChange={(e) =>
                handleAdministrationEnabledChange(e.target.checked)
              }
              disabled={isSaving}
            />
            <span>Enable Administration</span>
          </label>
          <div className="role-form__sub-permissions">
            <div className="role-form__sub-permissions-label">
              Field Administrator
            </div>
            <label className="role-form__checkbox-label role-form__checkbox-label--indented">
              <input
                type="checkbox"
                checked={formData.adminViewLicenses}
                onChange={(e) =>
                  handleChange("adminViewLicenses", e.target.checked)
                }
                disabled={isSaving || !formData.enableAdministration}
              />
              <span>View Licenses</span>
            </label>
            <label className="role-form__checkbox-label role-form__checkbox-label--indented">
              <input
                type="checkbox"
                checked={formData.adminConfigureAccessGroups}
                onChange={(e) =>
                  handleChange("adminConfigureAccessGroups", e.target.checked)
                }
                disabled={isSaving || !formData.enableAdministration}
              />
              <span>Configure Access Groups</span>
            </label>
            <label className="role-form__checkbox-label role-form__checkbox-label--indented">
              <input
                type="checkbox"
                checked={formData.adminConfigureRoles}
                onChange={(e) =>
                  handleChange("adminConfigureRoles", e.target.checked)
                }
                disabled={isSaving || !formData.enableAdministration}
              />
              <span>Configure Roles</span>
            </label>
            <label className="role-form__checkbox-label role-form__checkbox-label--indented">
              <input
                type="checkbox"
                checked={formData.adminConfigureUsers}
                onChange={(e) =>
                  handleChange("adminConfigureUsers", e.target.checked)
                }
                disabled={isSaving || !formData.enableAdministration}
              />
              <span>Configure Users</span>
            </label>
          </div>
        </div>

        {/* Module Configuration Tabs */}
        <div className="role-form__tabs">
          <div className="role-form__tabs-nav">
            <button
              type="button"
              className={`role-form__tab ${
                activeTab === "time" ? "role-form__tab--active" : ""
              }`}
              onClick={() => setActiveTab("time")}
              disabled={isSaving}
            >
              Time Entry
            </button>
            <button
              type="button"
              className={`role-form__tab ${
                activeTab === "expense" ? "role-form__tab--active" : ""
              }`}
              onClick={() => setActiveTab("expense")}
              disabled={isSaving}
            >
              Expense
            </button>
            <button
              type="button"
              className={`role-form__tab ${
                activeTab === "project" ? "role-form__tab--active" : ""
              }`}
              onClick={() => setActiveTab("project")}
              disabled={isSaving}
            >
              Project Management
            </button>
          </div>

          <div className="role-form__tabs-content">
            {activeTab === "time" && (
              <TimeTab
                formData={formData}
                onChange={handleChange}
                disabled={isSaving}
              />
            )}
            {activeTab === "expense" && (
              <ExpenseTab
                formData={formData}
                onChange={handleChange}
                disabled={isSaving}
              />
            )}
            {activeTab === "project" && (
              <ProjectTab
                formData={formData}
                onChange={handleChange}
                disabled={isSaving}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
